import React, { useState } from 'react';
import { useAuth } from '../../../../AuthContext';
import { changePassword } from '../../../../api/authApi';
import { validatePassword } from '../../../../utils/passwordValidation';
import SheetField from '../shared/SheetField';
import SheetMessage from '../shared/SheetMessage';
import PasswordStrengthMeter from '../shared/PasswordStrengthMeter';

interface ChangePasswordInlineProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Inline change-password form that expands above the identity footer when
 * its key icon is clicked. Success stays in the profile (no redirect).
 */
const ChangePasswordInline: React.FC<ChangePasswordInlineProps> = ({ open, onClose }) => {
  const { token } = useAuth();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [k: string]: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCurrent('');
    setPassword('');
    setConfirm('');
    setFieldErrors({});
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: { [k: string]: string } = {};
    if (!current) errs.current = 'Enter your current password';
    const pwErr = validatePassword(password);
    if (pwErr) errs.password = pwErr;
    if (password === current && password) errs.password = 'New password must differ from current';
    if (confirm !== password) errs.confirm = 'Passwords do not match';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0 || !token) return;

    setError(null);
    setSubmitting(true);
    try {
      await changePassword(token, current, password);
      setSuccess(true);
      reset();
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="tend-view-enter"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '4px 0 14px',
        marginBottom: 14,
        borderBottom: '1px solid rgba(255,255,255,.14)',
      }}
    >
      <div className="tend-micro">Change password</div>
      {success && <SheetMessage kind="ok">Password updated.</SheetMessage>}
      {error && <SheetMessage kind="err">{error}</SheetMessage>}
      <SheetField
        label="Current password"
        value={current}
        onChange={setCurrent}
        type="password"
        autoComplete="current-password"
        error={fieldErrors.current}
        disabled={submitting}
        autoFocus
      />
      <SheetField
        label="New password"
        value={password}
        onChange={setPassword}
        type="password"
        autoComplete="new-password"
        error={fieldErrors.password}
        disabled={submitting}
      />
      <PasswordStrengthMeter password={password} />
      <SheetField
        label="Confirm new password"
        value={confirm}
        onChange={setConfirm}
        type="password"
        autoComplete="new-password"
        error={fieldErrors.confirm}
        disabled={submitting}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          className="tend-btn tend-btn--cta"
          disabled={submitting}
          style={{ flex: 1 }}
        >
          {submitting ? 'Updating…' : 'Update Password'}
        </button>
        <button
          type="button"
          className="tend-btn tend-btn--glass"
          onClick={() => {
            reset();
            onClose();
          }}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordInline;
